import React from 'react';
import styled from 'styled-components';
import { PencilSquareIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Button } from '../../design-system/components/Button';
import { Heading, Text } from '../../design-system/components/Typography';
import { spacing } from '../../design-system/tokens/spacing';
import { borders } from '../../design-system/tokens/borders';
import { typography } from '../../design-system/tokens/typography';
import { animations } from '../../design-system/tokens/animations';
import { breakpoints } from '../../design-system/tokens/breakpoints';
import { hexToRgba } from '../../utils/colorUtils';
import type { CompletenessStatus } from '../../utils/profileSectionCompleteness';
import SectionCompletenessBadge from './SectionCompletenessBadge';

export interface ProfileSectionCompletenessProps {
  percentage: number;
  status: CompletenessStatus;
  statusLabel: string;
}

export interface ProfileSectionCardProps {
  id: string;
  title: string;
  description?: string;
  summary?: React.ReactNode;
  isEditing: boolean;
  disabled?: boolean;
  saving?: boolean;
  error?: string | null;
  editLabel: string;
  saveLabel: string;
  cancelLabel: string;
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  children: React.ReactNode;
  /** Optional per-section completeness indicator. */
  completeness?: ProfileSectionCompletenessProps;
}

const Section = styled.section<{ $editing: boolean; $disabled: boolean }>`
  border: ${borders.width.thin} solid
    ${props => (props.$editing ? props.theme.colors.primary : props.theme.colors.border)};
  border-radius: ${borders.radius.lg};
  background: ${props => props.theme.colors.surface};
  overflow: hidden;
  opacity: ${props => (props.$disabled ? 0.55 : 1)};
  pointer-events: ${props => (props.$disabled ? 'none' : 'auto')};
  transition: border-color ${animations.duration.fast} ${animations.easing.easeOut},
    box-shadow ${animations.duration.fast} ${animations.easing.easeOut};

  ${props =>
    props.$editing &&
    `
    box-shadow: 0 0 0 3px ${hexToRgba(props.theme.colors.primary, 0.12)};
  `}
`;

const AccentBar = styled.div<{ $status?: CompletenessStatus }>`
  height: 3px;
  background: ${props => {
    switch (props.$status) {
      case 'complete':
        return props.theme.colors.success;
      case 'partial':
        return props.theme.colors.icomGold || props.theme.colors.accent || props.theme.colors.warning;
      case 'notApplicable':
        return props.theme.colors.border;
      case 'incomplete':
        return props.theme.colors.error;
      default:
        return props.theme.colors.primary;
    }
  }};
`;

const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing[3]};
  padding: ${spacing[4]} ${spacing[5]};
  background: ${props =>
    props.theme.colors.surfaceHover || props.theme.colors.icomWarmGray || 'transparent'};

  ${breakpoints.down.md} {
    flex-direction: column;
    align-items: stretch;
    padding: ${spacing[4]};
  }
`;

const HeaderText = styled.div`
  flex: 1;
  min-width: 0;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[2]};
  flex-wrap: wrap;
`;

const Chevron = styled(ChevronDownIcon)<{ $open: boolean }>`
  width: 18px;
  height: 18px;
  color: ${props => props.theme.colors.textSecondary};
  transform: rotate(${props => (props.$open ? '180deg' : '0deg')});
  transition: transform ${animations.duration.fast} ${animations.easing.easeOut};
  flex-shrink: 0;
`;

const Description = styled(Text)`
  margin-top: ${spacing[1]};
  display: block;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing[3]};
  flex-shrink: 0;
  flex-wrap: wrap;

  ${breakpoints.down.md} {
    width: 100%;
    justify-content: space-between;
  }
`;

const Body = styled.div`
  padding: ${spacing[5]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};

  ${breakpoints.down.md} {
    padding: ${spacing[4]};
  }
`;

const Summary = styled.div`
  padding: ${spacing[4]} ${spacing[5]};
  color: ${props => props.theme.colors.textSecondary};
  font-size: ${typography.fontSize.sm};
  font-family: ${typography.fontFamily.primary};
  line-height: ${typography.lineHeight.relaxed};

  ${breakpoints.down.md} {
    padding: ${spacing[3]} ${spacing[4]};
  }
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${spacing[3]};
  margin-top: ${spacing[5]};
  padding-top: ${spacing[4]};
  border-top: ${borders.width.thin} solid ${props => props.theme.colors.border};

  ${breakpoints.down.md} {
    flex-direction: column-reverse;

    button {
      width: 100%;
    }
  }
`;

const ErrorText = styled(Text)`
  margin-bottom: ${spacing[3]};
  display: block;
`;

const EditIcon = styled(PencilSquareIcon)`
  width: 16px;
  height: 16px;
`;

export const ProfileSectionCard: React.FC<ProfileSectionCardProps> = ({
  id,
  title,
  description,
  summary,
  isEditing,
  disabled = false,
  saving = false,
  error,
  editLabel,
  saveLabel,
  cancelLabel,
  onEdit,
  onSave,
  onCancel,
  children,
  completeness,
}) => {
  return (
    <Section
      id={`profile-section-${id}`}
      $editing={isEditing}
      $disabled={disabled}
      aria-labelledby={`profile-section-title-${id}`}
    >
      <AccentBar $status={completeness?.status} />
      <Header>
        <HeaderText>
          <TitleRow>
            <Chevron $open={isEditing} aria-hidden />
            <span id={`profile-section-title-${id}`}>
              <Heading variant="h4" color="text">
                {title}
              </Heading>
            </span>
          </TitleRow>
          {description && (
            <Description size="sm" color="secondary">
              {description}
            </Description>
          )}
        </HeaderText>
        <HeaderActions>
          {completeness && (
            <SectionCompletenessBadge
              percentage={completeness.percentage}
              status={completeness.status}
              statusLabel={completeness.statusLabel}
              size="sm"
              ariaLabel={`${title}: ${completeness.statusLabel}`}
            />
          )}
          {!isEditing ? (
            <Button type="button" variant="outline" size="sm" onClick={onEdit} disabled={disabled}>
              <EditIcon aria-hidden />
              {editLabel}
            </Button>
          ) : null}
        </HeaderActions>
      </Header>

      {isEditing ? (
        <Body>
          {error && (
            <div role="alert">
              <ErrorText size="sm" color="error">
                {error}
              </ErrorText>
            </div>
          )}
          {children}
          <Footer>
            <Button type="button" variant="outline" onClick={onCancel} disabled={saving}>
              {cancelLabel}
            </Button>
            <Button type="button" variant="gradient" ripple loading={saving} onClick={onSave}>
              {saveLabel}
            </Button>
          </Footer>
        </Body>
      ) : (
        summary != null && <Summary>{summary}</Summary>
      )}
    </Section>
  );
};

export default ProfileSectionCard;
